/**
 * Settings React Query hooks
 * Handles fetching and updating application settings
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '@/stores/useNotificationStore';
import * as systemApi from '@/services/systemApi';
import type { ScraperSettings } from '@/types/api';

/**
 * Settings interface
 */
export interface Settings {
  scraper: ScraperSettings;
  notifications: {
    enabled: boolean;
    onJobComplete: boolean;
    onJobFailed: boolean;
    onError: boolean;
  };
}

/**
 * Query keys for settings
 */
export const settingsKeys = {
  all: ['settings'] as const,
  scraper: () => [...settingsKeys.all, 'scraper'] as const,
  notifications: () => [...settingsKeys.all, 'notifications'] as const,
  visibleSearchTerms: () => [...settingsKeys.all, 'visibleSearchTerms'] as const,
  visibleCategories: () => [...settingsKeys.all, 'visibleCategories'] as const,
  categoryCuration: () => [...settingsKeys.all, 'categoryCuration'] as const,
  nameBlacklist: () => [...settingsKeys.all, 'nameBlacklist'] as const,
  nameBlacklistActive: () => [...settingsKeys.all, 'nameBlacklistActive'] as const,
};

/**
 * Hook to fetch all settings
 */
export function useSettings() {
  return useQuery({
    queryKey: settingsKeys.all,
    queryFn: async (): Promise<Settings> => {
      const scraper = await systemApi.getScraperSettings();
      return {
        scraper,
        notifications: {
          enabled: true,
          onJobComplete: true,
          onJobFailed: true,
          onError: true,
        },
      };
    },
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to fetch scraper settings
 */
export function useScraperSettings() {
  return useQuery({
    queryKey: settingsKeys.scraper(),
    queryFn: systemApi.getScraperSettings,
    staleTime: 60000,
  });
}

/**
 * Hook to update settings
 */
export function useUpdateSettings() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: async (updates: Partial<Settings>) => {
      if (updates.scraper) {
        await systemApi.updateScraperSettings(updates.scraper);
      }
      return updates;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.all });
      success('Settings saved', 'Your settings have been updated');
    },
    onError: (err) => {
      error('Failed to save settings', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Default scraper settings - must match backend DEFAULT_SCRAPER_SETTINGS
 */
const DEFAULT_SCRAPER_SETTINGS: ScraperSettings = {
  maxWorkers: 3,
  concurrency: 5,
  maxBrowsers: 3,
  pageTimeout: 30000,
  navigationTimeout: 60000,
  waitTimeout: 10000,
  minDelay: 2000,
  maxDelay: 5000,
  scrollDelay: 1500,
  defaultZoom: 16,
  gridOverlap: 0.1,
  maxResultsPerCell: 120,
  headless: true,
};

/**
 * Hook to reset settings to defaults
 */
export function useResetSettings() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: async () => {
      // Reset to default scraper settings
      await systemApi.updateScraperSettings(DEFAULT_SCRAPER_SETTINGS);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.all });
      success('Settings reset', 'Settings have been reset to defaults');
    },
    onError: (err) => {
      error('Failed to reset settings', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to fetch visible search terms
 */
export function useVisibleSearchTerms() {
  return useQuery({
    queryKey: settingsKeys.visibleSearchTerms(),
    queryFn: async () => {
      const data = await systemApi.getVisibleSearchTerms();
      return data.visibleTerms;
    },
    staleTime: 60000,
  });
}

/**
 * Hook to update visible search terms
 */
export function useUpdateVisibleSearchTerms() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: async (visibleTerms: string[]) => {
      return systemApi.setVisibleSearchTerms(visibleTerms);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.visibleSearchTerms() });
      success('Visible terms updated', `${variables.length} search term(s) are now visible to users`);
    },
    onError: (err) => {
      error('Failed to update', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to fetch visible categories
 */
export function useVisibleCategories() {
  return useQuery({
    queryKey: settingsKeys.visibleCategories(),
    queryFn: async () => {
      const data = await systemApi.getVisibleCategories();
      return data.visibleCategories;
    },
    staleTime: 60000,
  });
}

/**
 * Hook to fetch category curation entries (for admin settings page)
 */
export function useCategoryCuration() {
  return useQuery({
    queryKey: settingsKeys.categoryCuration(),
    queryFn: async () => {
      const data = await systemApi.getCategoryCuration();
      return data.categories;
    },
    staleTime: 60000,
  });
}

/**
 * Hook to update category curation states
 */
export function useUpdateCategoryCuration() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: async (categories: systemApi.CategoryCurationEntry[]) => {
      return systemApi.setCategoryCuration(categories);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.categoryCuration() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.visibleCategories() });
      success('Category curation saved', `${data.included} included, ${data.excluded} excluded`);
    },
    onError: (err) => {
      error('Failed to update', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

// ===========================================================================
// NAME BLACKLIST
// ===========================================================================

/**
 * Hook to fetch name blacklist entries (admin settings page)
 */
export function useNameBlacklist() {
  return useQuery({
    queryKey: settingsKeys.nameBlacklist(),
    queryFn: async () => {
      const data = await systemApi.getNameBlacklist();
      return data.entries;
    },
    staleTime: 60000,
  });
}

/**
 * Hook to add a name blacklist entry
 */
export function useAddNameBlacklistEntry() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: async (pattern: string) => {
      return systemApi.addNameBlacklistEntry(pattern);
    },
    onSuccess: (_data, pattern) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.nameBlacklist() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.nameBlacklistActive() });
      success('Pattern added', `"${pattern}" added to name blacklist`);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      error('Failed to add pattern', msg.includes('409') ? 'Pattern already exists' : msg);
    },
  });
}

/**
 * Hook to remove a name blacklist entry
 */
export function useRemoveNameBlacklistEntry() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: async (id: number) => {
      return systemApi.removeNameBlacklistEntry(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.nameBlacklist() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.nameBlacklistActive() });
      success('Pattern removed', 'Name blacklist entry removed');
    },
    onError: (err) => {
      error('Failed to remove', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to check if name blacklist has any entries (available to all users)
 */
export function useNameBlacklistActive() {
  return useQuery({
    queryKey: settingsKeys.nameBlacklistActive(),
    queryFn: async () => {
      const data = await systemApi.getNameBlacklistActive();
      return data.active;
    },
    staleTime: 60000,
  });
}

export default useSettings;
